import React, { useState, useRef, useMemo, useCallback, useImperativeHandle, forwardRef } from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useUploader } from "@utils";
import { toast } from "sonner";
import AIReplyToolbar from "./AIReplyToolbar";
import RichTextEditor from "./RichTextEditor";
import ResourceMentionDropdown from "./ResourceMentionDropdown";
import useResourceMention from "./useResourceMention";
import TicketFilePreview from "./TicketFilePreview";
import TicketReplyActions from "./TicketReplyActions";

const stripHtml = (html) => html.replace(/<[^>]*>/g, "").trim();

const TicketReplyBox = forwardRef(function TicketReplyBox({ ticketId, onMessageSent }, ref) {
    const { t } = useTranslation();
    const [newMessage, setNewMessage] = useState("");
    const [aiReplyActive, setAiReplyActive] = useState(false);
    const [editReason, setEditReason] = useState("");
    const [mentionItems, setMentionItems] = useState([]);
    const [showMention, setShowMention] = useState(false);
    const [mentionPos, setMentionPos] = useState(null);
    const aiToolbarRef = useRef(null);
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const { call: addAdminReply, isLoading: isSending } = useBluelagoon({ method: "addAdminTicketReply" });
    const { search: searchResources } = useResourceMention();
    const { loading: isUploading, files: uploadedFiles, uploadFiles, removeFile, clearFiles } = useUploader({ isPublic: true });

    const aiState = aiToolbarRef.current?.getAIState?.();
    const isEdited = useMemo(() => {
        if (!aiReplyActive || !aiState?.lastAIReply) return false;
        return stripHtml(newMessage) !== stripHtml(aiState.lastAIReply);
    }, [aiReplyActive, newMessage, aiState?.lastAIReply]);

    const handleAtTrigger = useCallback((term, bounds) => {
        const results = searchResources(term);
        setMentionItems(results);
        setShowMention(results.length > 0);
        if (bounds) setMentionPos({ top: bounds.bottom + 4, left: bounds.left });
    }, [searchResources]);
    const handleAtDismiss = useCallback(() => setShowMention(false), []);
    const handleMentionSelect = useCallback((item) => { editorRef.current?.insertLink(item.title, item.url); setShowMention(false); }, []);
    const handleFileSelect = useCallback((e) => { if (e.target.files?.length) { uploadFiles(e.target.files); e.target.value = ""; } }, [uploadFiles]);

    useImperativeHandle(ref, () => ({
        focusEditor: () => editorRef.current?.focus(),
        triggerAIGenerate: () => aiToolbarRef.current?.triggerGenerate(),
        insertResource: (title, url) => editorRef.current?.insertLinkAtCursor(title, url),
    }), []);

    const handleSend = useCallback(async (shouldImproveAI = false) => {
        const completed = uploadedFiles.filter((f) => f.status === "completed");
        if (!stripHtml(newMessage) && !completed.length) return;
        const attachments = completed.map((f) => ({ url: f.permanentUrl || f.url, name: f.name, type: f.file?.type, size: f.file?.size }));
        const currentAI = aiToolbarRef.current?.getAIState?.();
        const wasEdited = currentAI?.hasAIReply && currentAI.lastAIReply && stripHtml(newMessage) !== stripHtml(currentAI.lastAIReply);
        try {
            const res = await addAdminReply({ id: ticketId, message: newMessage, attachments, isFromStaff: true });
            if (res?.data?.message) {
                if (currentAI?.hasAIReply && currentAI.lastAIReply) {
                    aiToolbarRef.current.logFeedback(wasEdited ? "edited" : "accepted", newMessage, wasEdited ? (editReason.trim() || "User edited AI reply") : undefined);
                    aiToolbarRef.current.clearAIState();
                    setAiReplyActive(false); setEditReason("");
                }
                onMessageSent(res.data.message); setNewMessage(""); clearFiles();
                toast.success(t("admin.support.messageSent", "Message sent"));
                if (wasEdited && shouldImproveAI && currentAI.lastAIReply) aiToolbarRef.current?.analyzeAndShowModal(stripHtml(currentAI.lastAIReply), editReason.trim() || "User edited AI reply");
            }
        } catch { toast.error(t("admin.support.sendError", "Failed to send message")); }
    }, [newMessage, editReason, ticketId, addAdminReply, onMessageSent, t, uploadedFiles, clearFiles]);

    const hasContent = stripHtml(newMessage) || uploadedFiles.some((f) => f.status === "completed");
    return (
        <Card className="p-3 sm:p-4">
            <h3 className="font-medium mb-3 flex items-center gap-2">
                {t("admin.support.reply", "Reply")}
                <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded border border-border/60 text-muted-foreground font-normal" title={t("admin.support.startReplyHint", "Press Enter to focus the editor")}>↵</kbd>
            </h3>
            <AIReplyToolbar ref={aiToolbarRef} ticketId={ticketId} newMessage={newMessage} setNewMessage={setNewMessage} onAIStateChange={setAiReplyActive} />
            <div className="mb-3 relative">
                <RichTextEditor ref={editorRef} value={newMessage} onChange={setNewMessage}
                    placeholder={t("admin.support.replyPlaceholder", "Type your response... Use @ to insert resource links")}
                    onAtTrigger={handleAtTrigger} onAtDismiss={handleAtDismiss} />
                {showMention && <ResourceMentionDropdown items={mentionItems} onSelect={handleMentionSelect} onDismiss={handleAtDismiss} position={mentionPos} />}
            </div>
            <TicketFilePreview files={uploadedFiles} onRemove={removeFile} />
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
            {isEdited && (
                <input type="text" value={editReason} onChange={(e) => setEditReason(e.target.value)}
                    placeholder={t("admin.support.editReasonPlaceholder", "What did you change? (optional)")}
                    className="w-full mb-2 px-3 py-2 text-sm border rounded-md bg-amber-50 border-amber-200 placeholder:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-300" />
            )}
            <TicketReplyActions isEdited={isEdited} isSending={isSending} isUploading={isUploading}
                hasContent={hasContent} onSend={handleSend} onAttach={() => fileInputRef.current?.click()} />
        </Card>
    );
});

export default TicketReplyBox;
